import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { BEAUTY_CAT, FASHION_CAT, THRIFT_CAT, withKidsTag, hasKidsTag, KIDS_AGES, ageOf, ageLabel, withAgeTag } from "@/lib/lamka";
import { Switch } from "@/components/ui/switch";
import { withAdminUnlock, updateListing, adminSetCategory } from "@/lib/admin";

const FIELDS = [
  ["title", "Title"], ["price", "Price (₹)", "number"], ["stock", "Stock", "number"], ["size", "Size"],
  ["colour", "Colour"], ["delivery_time", "Delivery time"], ["location", "Location"],
];
const errMsg = (e) => e?.response?.data?.detail || "Something went wrong. Please try again.";

export const EditListingDialog = ({ item, open, onOpenChange, onUpdated, askPassword }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && item) setForm(Object.fromEntries([...FIELDS.map(([k]) => [k, item[k] ?? ""]), ["description", item.description || ""], ["category", item.category]]));
  }, [open, item]);

  if (!item) return null;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...form, price: Number(form.price) || 0, stock: form.stock === "" ? null : Number(form.stock) };
    try {
      let updated;
      let categoryOnly = false;
      try {
        updated = await withAdminUnlock(() => updateListing(item.id, body), askPassword);
      } catch (err) {
        // Main backend may only allow the owner to edit; admins can still fix the category.
        if (err?.response?.status === 403 && form.category !== item.category) {
          await withAdminUnlock(() => adminSetCategory(item.id, form.category), askPassword);
          updated = { ...item, category: form.category };
          categoryOnly = true;
        } else throw err;
      }
      onUpdated(updated?.id ? updated : { ...item, ...body });
      onOpenChange(false);
      if (categoryOnly) toast("Category updated. Other details can only be edited by the seller.");
      else toast.success("Listing updated");
    } catch (err) {
      if (err.message !== "cancelled") toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const base = [BEAUTY_CAT, FASHION_CAT, THRIFT_CAT];
  const cats = [...base, ...(item.category && !base.includes(item.category) ? [item.category] : [])];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-edit-dialog" className="rounded-none max-w-lg bg-[#FBF9F5] max-h-[90vh] overflow-y-auto">
        <DialogTitle className="font-display text-2xl">Edit listing</DialogTitle>
        <form onSubmit={save} className="grid grid-cols-2 gap-4 mt-2">
          {FIELDS.map(([k, label, type]) => (
            <label key={k} className={k === "title" ? "col-span-2" : ""}>
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">{label}</span>
              <input data-testid={`admin-edit-${k}`} type={type || "text"} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full bg-white border border-[#0F0F0F]/15 focus:border-[#C5A059] outline-none px-3 py-2 text-sm" />
            </label>
          ))}
          <label className="col-span-2">
            <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Category</span>
            <select data-testid="admin-edit-category" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value, ...(e.target.value !== THRIFT_CAT && hasKidsTag(form.description) && { description: withKidsTag(form.description, false) }) })} className="w-full bg-white border border-[#0F0F0F]/15 px-3 py-2 text-sm">
              {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          {form.category === THRIFT_CAT && (
            <div className="col-span-2 border border-[#0F0F0F]/10 bg-white px-3 py-2.5 space-y-2.5">
              <label className="flex items-center justify-between gap-3">
                <span className="text-sm">For kids <span className="text-xs text-[#71717A]">(shows on the Kids Thrift rail)</span></span>
                <Switch data-testid="admin-edit-kids-switch" checked={hasKidsTag(form.description)} onCheckedChange={(on) => setForm({ ...form, description: withKidsTag(form.description, on) })} />
              </label>
              {hasKidsTag(form.description) && (
                <div className="grid grid-cols-4 gap-1.5" role="radiogroup" aria-label="Age group">
                  {KIDS_AGES.map((a) => {
                    const on = ageOf(form.description) === a;
                    return <button key={a} type="button" role="radio" aria-checked={on} data-testid={`admin-edit-age-${a}`} onClick={() => setForm({ ...form, description: withAgeTag(form.description, on ? null : a) })} className={`h-9 border text-xs transition-colors ${on ? "border-[#C5A059] bg-[#C5A059]/15" : "border-[#0F0F0F]/15 text-[#71717A] hover:border-[#C5A059]/60"}`}>{ageLabel(a)}</button>;
                  })}
                </div>
              )}
            </div>
          )}
          <label className="col-span-2">
            <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Description</span>
            <textarea data-testid="admin-edit-description" rows={4} value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-white border border-[#0F0F0F]/15 focus:border-[#C5A059] outline-none px-3 py-2 text-sm" />
          </label>
          <button data-testid="admin-edit-save" type="submit" disabled={busy || !String(form.title || "").trim()} className="col-span-2 bg-[#0F0F0F] text-[#C5A059] py-3 text-xs tracking-[0.3em] uppercase disabled:opacity-40">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
